import React from 'react';
import { Box } from '@mui/material';
import AnnualActionAnalytic from '../charts/AnnualActionAnalytic';
import ActionAnalytics from '../charts/ActionAnalytics';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ActionDurationFilter from '../filters/ActionDurationFilter';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { DurationEnum, ActionType, RoleType, ALL } from '../../types/analyticTypes';
import { utils_instance } from '../../utils/Utils';
import { ArcElement } from 'chart.js';
import Chart from 'chart.js/auto';
import { ApiInstance } from '../../api/api';
import s from '../charts/analytic.module.css';
import pageStyles from './pageStyles';
import { useActionLogsPageQueryParams } from '../../hooks/useActionLogsPageQueryParams';
import { useRequiredSearchParams } from '../../hooks/useRequiredSearchParams';
import { getEnumParam, getEnumListParam, getStringListParam } from '../../utils/queryUtils';
import { useLoaderData } from 'react-router-dom';
import type { ActionLogUsersData } from '../../loaders/actionLogUsersLoader';
import { isEmailValidForRole } from '../filters/getUsersForRole';

Chart.register(ArcElement);

function AnalyticPage() {
  const [isLoading, setIsLoading] = React.useState(false);
  const [apiErrorFlag, setApiErrorFlag] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const [actionData, setActionData] = React.useState<number[]>([]);
  const [actionLabels, setActionLabels] = React.useState<string[]>(
    utils_instance.chartDataActionInitialState.labels
  );
  const [sumOfActionCounts, setSumOfActionCounts] = React.useState<number>(0);

  const { setParams } = useActionLogsPageQueryParams();
  const { userMap } = useLoaderData() as ActionLogUsersData;
  const { params } = useRequiredSearchParams(['duration', 'actions', 'role', 'users']);
  const durationQueryParam = getEnumParam(DurationEnum, params.duration);
  const actionsQueryParam = getEnumListParam(ActionType, params.actions);
  const roleQueryParam = getEnumParam(RoleType, params.role);
  const usersQueryParam = getStringListParam(params.users);

  const [periodName, setDurationOption] = React.useState<DurationEnum>(
    durationQueryParam || DurationEnum.day
  );
  const [actionTypes, setActionTypes] = React.useState<ActionType[]>(
    actionsQueryParam || [ActionType.all]
  );
  const [role, setRole] = React.useState<RoleType>(roleQueryParam || RoleType.all);
  const [users, setUsers] = React.useState<string[]>(usersQueryParam || [ALL]);

  const handleFilterChange = (
    selectedOption: DurationEnum,
    selectedActions: ActionType[],
    selectedRole: RoleType,
    selectedUsers: string[]
  ) => {
    setDurationOption(selectedOption);
    setActionTypes(selectedActions);
    setRole(selectedRole);
    setUsers(selectedUsers);
    setParams({
      duration: selectedOption,
      actions: selectedActions.join(','),
      role: selectedRole,
      users: selectedUsers.join(','),
    });
    loadData(selectedOption, selectedActions, selectedRole, selectedUsers);
  };

  const loadData = async (
    period: DurationEnum,
    actions: ActionType[],
    role: RoleType,
    users: string[]
  ) => {
    setSumOfActionCounts(0);
    setActionData([]);
    setActionLabels(['No actions']);

    if (period === DurationEnum.year) {
      return null;
    }

    try {
      setIsLoading(true);
      setApiErrorFlag(false);

      const actionAnalytics = await ApiInstance.getActionAnalytics(period, actions, role, users);

      if (actionAnalytics.actions.length === 0) {
        return;
      } else {
        setSumOfActionCounts(
          actionAnalytics.actions.reduce((accumulator, currentValue) => {
            return accumulator + currentValue.action_count;
          }, 0)
        );
        const data: number[] = [];
        const labels: string[] = [];
        actionAnalytics.actions.map((item) => {
          data.push(item.action_count);
          labels.push(item.user_action);
        });
        setActionLabels(labels);
        setActionData(data);
      }
    } catch (e: any) {
      setApiErrorFlag(true);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (
      durationQueryParam &&
      actionsQueryParam &&
      roleQueryParam &&
      usersQueryParam &&
      isEmailValidForRole(userMap, role, users)
    ) {
      loadData(periodName, actionTypes, role, users);
    } else {
      setError(
        'The link you used contains invalid filter options. Please select new values from the filter panel below.'
      );
      setOpenSnackbar(true);
    }
  }, []);

  return (
    <>
      <Typography sx={pageStyles.label} component="h1" variant="h4" align="center">
        <InfoOutlinedIcon fontSize="medium" color="primary" />
        Action analytics
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '10pt',
        }}
      >
        <ActionDurationFilter
          option={periodName}
          actions={actionTypes}
          role={role}
          users={users}
          userMap={userMap}
          onFilterChange={handleFilterChange}
        />
      </Box>
      {periodName === DurationEnum.year ? (
        <AnnualActionAnalytic actions={actionTypes} role={role} users={users} />
      ) : (
        <div className={`${s.paper_style}`} key={3}>
          <ActionAnalytics
            periodName={periodName}
            dataSet={actionData}
            labelSet={actionLabels}
            sumOfActionCounts={sumOfActionCounts}
            isLoading={isLoading}
            apiErrorFlag={apiErrorFlag}
          />
        </div>
      )}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={10000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}

export default AnalyticPage;
